import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router'

import { api, can, type BucketRow, type SourceTable } from '../api'
import { Pill, RunStatus } from '../components'
import { DataGrid, type BulkAction, type GridFilter } from '../DataGrid'
import { dt, num, statusKind } from '../format'
import { pollWhile } from '../hooks'

const opts = (...v: string[]) => v.map((x) => ({ value: x, label: x }))

const bucketFilters: GridFilter<BucketRow>[] = [
  { key: 'build', label: 'Last build', options: opts('succeeded', 'failed', 'skipped', 'cancelled', 'never'), value: (b) => b.last_run_status ?? 'never' },
  {
    key: 'builds',
    label: 'Builds',
    options: [
      { value: 'on', label: 'on' },
      { value: 'paused', label: 'paused' },
    ],
    value: (b) => (b.enabled ? 'on' : 'paused'),
  },
  {
    key: 'licence',
    label: 'Licence',
    options: opts('complete', 'incomplete'),
    value: (b) => (b.license?.complete ? 'complete' : 'incomplete'),
  },
]

export function LicenseBadge({ license }: { license: BucketRow['license'] }) {
  if (!license) return <span className="muted">–</span>
  return (
    <span className="cell-license">
      <span>{license.licenses.join(' + ') || '–'}</span>
      <span className="muted mono">{license.usage_mode}</span>
      {license.complete ? <Pill kind="ok">complete</Pill> : <Pill kind="err">incomplete</Pill>}
    </span>
  )
}

export function Buckets() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const editor = can(me.data, 'editor')
  const actions = useMemo<BulkAction<BucketRow>[]>(
    () =>
      editor
        ? [
            { label: 'Build now', primary: true, applies: (b) => !b.active_run, run: (b) => api.triggerBuild(b.slug) },
            { label: 'Pause builds', applies: (b) => b.enabled, run: (b) => api.patchBucket(b.slug, { enabled: false }) },
            { label: 'Resume builds', applies: (b) => !b.enabled, run: (b) => api.patchBucket(b.slug, { enabled: true }) },
          ]
        : [],
    [editor],
  )
  const q = useQuery({
    queryKey: ['buckets'],
    queryFn: api.buckets,
    refetchInterval: (query) => pollWhile(!!query.state.data?.some((b) => b.active_run), 3_000, 30_000),
  })
  const tables = useQuery({ queryKey: ['source-tables'], queryFn: api.sourceTables })

  const cols = useMemo<ColDef<BucketRow>[]>(
    () => [
      {
        field: 'title',
        headerName: 'Bucket',
        flex: 2,
        minWidth: 240,
        cellRenderer: ({ data }: ICellRendererParams<BucketRow>) =>
          data ? (
            <span className="cell-two">
              <Link to={`/buckets/${data.slug}`}>{data.title}</Link>
              <span className="muted mono">{data.slug}</span>
            </span>
          ) : null,
      },
      { headerName: 'Inputs', flex: 1, minWidth: 180, valueGetter: (p) => p.data?.inputs.map((i) => i.split(':')[1]).join(', '), cellClass: 'mono' },
      { headerName: 'Rows', width: 120, valueGetter: (p) => p.data?.last_build?.rows ?? null, valueFormatter: (p) => num(p.value), cellClass: 'num' },
      { headerName: 'Data from', width: 140, valueGetter: (p) => p.data?.last_build?.data_fetched_at, valueFormatter: (p) => dt(p.value) },
      {
        headerName: 'Last build',
        width: 250,
        valueGetter: (p) => p.data?.last_run_at,
        cellRenderer: ({ data }: ICellRendererParams<BucketRow>) =>
          data?.active_run ? (
            <RunStatus run={data.active_run} />
          ) : data?.last_run_at ? (
            <span>
              {dt(data.last_run_at)} <Pill kind={statusKind(data.last_run_status)}>{data.last_run_status}</Pill>
            </span>
          ) : (
            <span className="muted">never built</span>
          ),
      },
      {
        headerName: 'Licence',
        flex: 2,
        minWidth: 260,
        sortable: false,
        cellRenderer: ({ data }: ICellRendererParams<BucketRow>) => (data ? <LicenseBadge license={data.license} /> : null),
      },
      { field: 'storage', width: 100, cellClass: 'mono' },
      {
        field: 'enabled',
        headerName: 'Builds',
        width: 100,
        cellRenderer: ({ value }: ICellRendererParams<BucketRow, boolean>) => (value ? <Pill kind="ok">on</Pill> : <Pill kind="warn">paused</Pill>),
      },
      { field: 'spec_version', headerName: 'Spec', width: 80, valueFormatter: (p) => `v${p.value}`, cellClass: 'mono' },
    ],
    [],
  )

  const tableCols = useMemo<ColDef<SourceTable>[]>(
    () => [
      { field: 'slug', headerName: 'Table', width: 160, cellClass: 'mono' },
      { field: 'source', width: 120, cellRenderer: ({ value }: ICellRendererParams<SourceTable, string>) => <Link to={`/sources/${value}`}>{value}</Link> },
      { field: 'description', flex: 2, minWidth: 260, tooltipField: 'description' },
      { headerName: 'Columns', width: 100, valueGetter: (p) => p.data?.columns.length, cellClass: 'num' },
      { field: 'objects', headerName: 'Objects', width: 100, valueFormatter: (p) => num(p.value), cellClass: 'num' },
      { field: 'rows', headerName: 'Rows (latest)', width: 140, valueFormatter: (p) => num(p.value), cellClass: 'num' },
      { field: 'latest_at', headerName: 'Latest data', width: 140, valueFormatter: (p) => dt(p.value) },
      { field: 'produced_by', headerName: 'From', width: 100 },
    ],
    [],
  )

  return (
    <section className="page">
      <header className="page-h">
        <div>
          <h1>Buckets</h1>
          <p className="sub">
            Tables built from source tables (or other buckets) by a versioned build spec. A bucket rebuilds when an input changes;
            rows that fail its rules are quarantined, never dropped.
          </p>
        </div>
      </header>
      {q.error && <p className="callout err">{q.error.message}</p>}
      <DataGrid<BucketRow>
        rows={q.data}
        columnDefs={cols}
        loading={q.isLoading}
        getRowId={(b) => b.slug}
        searchText={(b) => [b.slug, b.title, ...b.inputs, ...(b.license?.licenses ?? [])].join(' ')}
        filters={bucketFilters}
        actions={actions}
        onDone={() => qc.invalidateQueries({ queryKey: ['buckets'] })}
        onOpen={(b) => navigate(`/buckets/${b.slug}`)}
      />

      <div className="card">
        <h2>Source tables</h2>
        <p className="note">What ingests and extractions produce, and what build specs read. Each table is a set of Parquet objects.</p>
        <DataGrid<SourceTable>
          prefix="t"
          rows={tables.data}
          columnDefs={tableCols}
          rowHeight={42}
          loading={tables.isLoading}
          getRowId={(t) => t.slug}
          searchText={(t) => [t.slug, t.source, t.description, ...t.columns.map((c) => c.name)].join(' ')}
          filters={[{ key: 'from', label: 'From', options: opts('ingest', 'extraction'), value: (t) => t.produced_by }]}
        />
      </div>
    </section>
  )
}
